import { Badge, Button, Card, MonoLabel, cn } from '@/design-system';
import { useBreakpoint } from '@/shared/lib';

import { SurveyActionButton } from './components/SurveyActionButton';
import { myDayCopy } from './copy';
import type { LocalStop } from './MyDayModel';
import { surveyActionForStop } from './surveyAction';

export interface MyDayStopProps {
    stop: LocalStop;
    onStart: (stop: LocalStop) => void;
    onOpenOrder: (stop: LocalStop) => void;
}

/**
 * Eén stop uit de lokale opslag: tijd en duur links, wat het is in het midden,
 * rechts de twee knoppen. De bovenste knop opent de juiste opname - ook
 * zonder netwerk, want alles staat op het apparaat.
 *
 * Een afspraak zonder opname, zoals een energielabel zonder vragenlijst, heeft
 * die knop niet: er valt in de app niets te starten, en dat staat erbij.
 *
 * Op een telefoon vallen die drie kolommen onder elkaar: naast elkaar werd het
 * adres afgekapt en liepen de knoppen door de tekst heen. De tijd staat dan op
 * één regel met de duur, en de knoppen liggen over de volle breedte — waar je
 * duim toch al is.
 */
export function MyDayStop({ stop, onStart, onOpenOrder }: MyDayStopProps) {
    const { isPhone } = useBreakpoint();

    return (
        <Card
            padding="md"
            className={cn(
                'grid gap-4',
                isPhone
                    ? 'grid-cols-1 gap-3'
                    : 'items-center [grid-template-columns:72px_minmax(0,1fr)_auto]',
            )}
        >
            <div className={cn(isPhone && 'flex items-baseline gap-3')}>
                <div className="font-[family-name:var(--font-display)] text-[19px] leading-[1.1] font-semibold text-[var(--text-primary)]">
                    {stop.time ?? myDayCopy.stopTimeUnknown}
                </div>
                <MonoLabel
                    size={11}
                    variant="value"
                    tone="default"
                    as="div"
                    className={cn(!isPhone && 'mt-[2px]')}
                >
                    {stop.date ??
                        (stop.durationMinutes !== null
                            ? myDayCopy.stopDuration(stop.durationMinutes)
                            : '')}
                </MonoLabel>
            </div>

            <div
                className={cn(
                    'min-w-0',
                    !isPhone && 'border-l border-[var(--border-subtle)] pl-4',
                )}
            >
                <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Badge variant="mono">{stop.normLabel}</Badge>
                    <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                        {stop.orderNumber}
                    </span>
                    {stop.submitted ? (
                        <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                            {myDayCopy.stopSubmitted}
                        </span>
                    ) : null}
                    {stop.inspectionUuid === null ? (
                        <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                            {myDayCopy.stopNoSurvey}
                        </span>
                    ) : stop.ready ? null : (
                        <span className="text-[12px] text-[var(--warn-text)]">
                            {myDayCopy.stopNotOnDevice}
                        </span>
                    )}
                </div>
                <div className="text-[15px] leading-[1.35] font-medium text-[var(--text-primary)]">
                    {stop.address ?? myDayCopy.stopNoAddress}
                </div>
                {stop.clientName ? (
                    <div className="mt-[3px] text-[13px] text-[var(--text-tertiary-safe)]">
                        {stop.clientName}
                    </div>
                ) : null}
            </div>

            <div
                className={cn(
                    'flex flex-col items-stretch gap-[7px]',
                    !isPhone && 'min-w-[150px]',
                )}
            >
                {/* Dezelfde knop als in de lijst Keuringen en in de kop van één keuring;
                    hier opent hij de opname van het apparaat, dus ook zonder verbinding.
                    De regel "geen opname in de app" staat al in het blok hiernaast. */}
                <SurveyActionButton
                    action={surveyActionForStop(stop)}
                    quiet
                    fullWidth
                    onOpen={() => onStart(stop)}
                />
                <Button
                    variant="outline"
                    size={isPhone ? 'md' : 'sm'}
                    onClick={() => onOpenOrder(stop)}
                >
                    {myDayCopy.stopOpenOrder}
                </Button>
            </div>
        </Card>
    );
}
